import React, { useRef, useState } from "react";
import { toast } from "./ui/sonner";
import { chunkUpload } from "./EnginePanel";
import { evidenceUrl } from "./EvidenceLightbox";
import { CheckCircle2, Loader2, Mic } from "lucide-react";

export const CompleteNoteModal = ({ title, zh, api, onConfirm, onClose }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(null);
  const [audio, setAudio] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startAtRef = useRef(0);

  const startHold = async (e) => {
    e.preventDefault();
    if (rec) return;
    if (!navigator.mediaDevices?.getUserMedia) { toast.error(zh ? "当前浏览器不支持录音" : "Recording not supported"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const opts = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? { mimeType: "audio/webm" } : undefined;
      const mr = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 800) { setRec(null); toast.error(zh ? "录音太短，请按住说话" : "Too short — hold to talk"); return; }
        setRec("stt");
        const durationSec = Math.max(1, Math.round((Date.now() - startAtRef.current) / 1000));
        try {
          const file = new File([blob], `note_${Date.now()}.webm`, { type: blob.type || "audio/webm" });
          const up = await chunkUpload(api, file);
          setAudio({ fileId: up.fileId, durationSec });
          const { data } = await api.post("/engine/transcribe", { fileId: up.fileId });
          const text = (data.text || "").trim();
          if (text) setNote((n) => (n ? n + " " : "") + text);
          else toast.error(zh ? "未识别到语音内容，录音已保留可回放" : "Nothing recognized — audio kept");
        } catch (err) { toast.error(err?.response?.data?.detail || (zh ? "语音转写失败（录音已保留）" : "Transcribe failed")); }
        setRec(null);
      };
      mr.start(250);
      mediaRef.current = mr;
      startAtRef.current = Date.now();
      setRec("rec");
    } catch { toast.error(zh ? "无法访问麦克风，请授权后重试" : "Mic access denied"); }
  };
  const stopHold = () => { if (rec === "rec") { try { mediaRef.current?.stop(); } catch {} } };
  const confirm = async () => { setBusy(true); try { await onConfirm(note.trim(), audio); } finally { setBusy(false); } };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={busy ? undefined : onClose} data-testid="complete-note-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
        <p className="font-bold text-slate-800 text-sm flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" />{zh ? "完成打卡确认" : "Complete task"}</p>
        <p className="text-xs text-slate-500 mt-1.5" data-testid="complete-note-task-title">{zh ? `请确认「${title}」的实际操作已在现场真实完成。` : `Confirm "${title}" is actually completed on site.`}</p>
        <p className="text-[10px] text-slate-400 mt-2.5 mb-1">{zh ? "现场说明（选填）— 可打字或按住 🎙 说话；文字与原始录音将一并记入任务进度日志" : "Optional site note — type or hold mic; text + audio saved to task log"}</p>
        <textarea data-testid="complete-note-input" autoFocus value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          placeholder={zh ? "例如：石膏板已全部封板，现场已清理" : "e.g. drywall closed up, site cleaned"}
          className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5" />
        {audio && (
          <div className="flex items-center gap-1.5 mt-1.5" data-testid="complete-note-audio-row">
            <audio controls preload="none" className="h-8 flex-1 min-w-0" data-testid="complete-note-audio" src={evidenceUrl(audio.fileId)} />
            <span className="text-[10px] text-slate-400 shrink-0">{audio.durationSec}s</span>
            <button data-testid="complete-note-audio-remove" onClick={() => setAudio(null)} disabled={busy}
              className="text-slate-300 hover:text-red-500 text-sm shrink-0" title={zh ? "移除录音" : "Remove audio"}>✕</button>
          </div>
        )}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <button type="button" data-testid="complete-note-mic"
            onMouseDown={startHold} onMouseUp={stopHold} onMouseLeave={stopHold}
            onTouchStart={startHold} onTouchEnd={stopHold}
            disabled={rec === "stt" || busy}
            className={`select-none text-[11px] font-semibold rounded-lg px-3 py-2 inline-flex items-center gap-1.5 transition-colors ${rec === "rec" ? "bg-red-600 text-white animate-pulse" : rec === "stt" ? "bg-slate-100 text-slate-400" : "bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200"}`}>
            {rec === "stt" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
            {rec === "rec" ? (zh ? "松开结束…" : "Release to stop") : rec === "stt" ? (zh ? "转写中…" : "Transcribing…") : (zh ? "按住说话" : "Hold to talk")}
          </button>
          <div className="flex-1" />
          <button data-testid="complete-note-cancel" onClick={onClose} disabled={busy} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-2">{zh ? "取消" : "Cancel"}</button>
          <button data-testid="complete-note-confirm" onClick={confirm} disabled={busy || rec !== null}
            className="text-xs font-semibold rounded-lg px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}{zh ? "确定完成" : "Complete"}</button>
        </div>
      </div>
    </div>
  );
};
